<script setup>
    import { ref, onMounted, computed } from "vue";
    import { useStore } from 'vuex';
    import { db, storage } from '@/firebase';
    import { collection, query, where, getDocs, doc, getDoc } from 'firebase/firestore';
    import { ref as firebaseRef, uploadBytes, getDownloadURL } from 'firebase/storage';
    import Users from '@/api/users';

    import CreateUser from "@/components/modals/CreateUser.vue";
    import GenericTable2 from '@/components/tables/GenericTable2.vue';
    import ConfirmModal from "@/components/modals/ConfirmModal.vue";

    
    const company = ref({});
    const companyUsers = ref([]);
    const createUserModal = ref(false);
    const editUserModal = ref(false);
    const selectedUser = ref(null);
    const loading = ref(false);
    const showDeleteModal = ref(false);
    const pendingDeleteUID = ref(null);
    const deleteLoading = ref(false);
    const deleteError = ref('');

    // Grab stored values
    const store = useStore();
    const user = store.state.user;

    // Check if user has permission to view this page
    const hasAccess = computed(() => {
        return user && (
            user.role === 'admin' || 
            (user.permissions && (
                user.permissions.includes('addCompanyUser') ||
                user.permissions.includes('editCompanyUser') ||
                user.permissions.includes('deleteCompanyUser')
            ))
        );
    });

    // Check individual permissions
    const canEdit = computed(() => {
        return user && (
            user.role === 'admin' || 
            (user.permissions && user.permissions.includes('editCompanyUser'))
        );
    });

    const canDelete = computed(() => {
        return user && (
            user.role === 'admin' || 
            (user.permissions && user.permissions.includes('deleteCompanyUser'))
        );
    });

    // Table actions based on permissions
    const tableActions = computed(() => {
        const actions = [];
        if (canEdit.value) actions.push('Edit');
        if (canDelete.value) actions.push('Delete');
        return actions;
    });

    // Format data for GenericTable2
    const tableData = computed(() => {
        return companyUsers.value.map((companyUser) => ({
            id: companyUser.uid,
            name: `${companyUser.firstName} ${companyUser.lastName}`,
            email: companyUser.email,
            role: companyUser.role,
            status: companyUser.status
        }));
    });

    // Check if user can add new users
    const canAdd = computed(() => {
        return user && (
            user.role === 'admin' || 
            (user.permissions && user.permissions.includes('addCompanyUser'))
        );
    });

    // Prevent actions on current user (can't edit or delete yourself)
    const actionException = computed(() => {
        const currentUserIndex = companyUsers.value.findIndex(u => u.uid === user.uid);
        return currentUserIndex !== -1 ? [currentUserIndex] : [];
    });

    const deleteModalMessage = computed(() => {
        if (!pendingDeleteUID.value) return 'Are you sure you want to delete this user?';
        const target = companyUsers.value.find(u => u.uid === pendingDeleteUID.value);
        return target
            ? `Are you sure you want to delete ${target.firstName} ${target.lastName}?`
            : 'Are you sure you want to delete this user?';
    });

    onMounted(async() => {
        try {
            const companyQry = doc(db, "entities", user.entity);
            const companySnapshot = await getDoc(companyQry);
            company.value = companySnapshot.data();

            const qry = query(collection(db, "users"), where("entity", "==", user.entity));
            const querySnapshot = await getDocs(qry);
            querySnapshot.forEach((doc) => {
                const data = doc.data();
                data['uid'] = doc.id;
                companyUsers.value.push(data)
            });
        } catch (error) {
            console.error('Error loading company users:', error);
        }
    });
    
    const uploadImage = async (folder, file) => {
        if (!file) return "";
        let imageURL = null;
        const storageRef = firebaseRef(storage, `${folder}/${file.name}`);
        await uploadBytes(storageRef, file).then(() => {
            return getDownloadURL(storageRef);
        }).then((url) => {
            imageURL = url;
        });
        return imageURL;
    };

    const addCompanyUser = async (data) => {
        let responseData;
        const entityID = user.entity;
        
        const body = {
            uid: user.uid,
            firstName: data.firstName,
            lastName: data.lastName,
            email: data.email,
            role: data.role,
            entity: entityID,
            company: data.company,
            timeFormat: data.timeFormat || "24Hour",
            permissions: data.permissions || [],
            units: data.units || [],
            profilePic: await uploadImage('profile', data.profilePic) || "",
            status: 'active'
        }

        try {
            responseData = await Users.post('', body);
        } catch (error) {
            console.error('Error creating company user:', error);
        }

        createUserModal.value = false;
        if (responseData.status === 'success') {
            body.uid = responseData.details.newUserUID;
            companyUsers.value.push(body);
        }
        setTimeout(() => {
            if (responseData.status !== 'success') {
                try {
                    alert(responseData.message);
                } catch (error) {
                    alert("Something went wrong when creating the user");
                }
            }
        }, 100);
    };

    const editCompanyUser = async (data) => {
        let responseData;
        const entityID = user.entity;
        
        const body = {
            uid: user.uid,
            userUID: selectedUser.value.uid,
            firstName: data.firstName,
            lastName: data.lastName,
            email: data.email,
            role: data.role,
            entity: entityID,
            company: data.company,
            timeFormat: data.timeFormat || "24Hour",
            permissions: data.permissions || [],
            units: data.units || [],
            status: data.status || selectedUser.value.status || 'active'
        }

        // Handle profile picture if changed
        if (data.profilePic) {
            body.profilePic = await uploadImage('profile', data.profilePic) || selectedUser.value.profilePic || "";
        } else {
            body.profilePic = selectedUser.value.profilePic || "";
        }

        try {
            responseData = await Users.put('', body);
        } catch (error) {
            console.error('Error updating company user:', error);
        }

        editUserModal.value = false;
        if (responseData.status === 'success') {
            // Update the user in the local array
            const userIndex = companyUsers.value.findIndex(u => u.uid === selectedUser.value.uid);
            if (userIndex !== -1) {
                companyUsers.value[userIndex] = {
                    ...companyUsers.value[userIndex],
                    ...body,
                    uid: selectedUser.value.uid
                };
            }
        }
        selectedUser.value = null;
        setTimeout(() => {
            if (responseData.status !== 'success') {
                try {
                    alert(responseData.message);
                } catch (error) {
                    alert("Something went wrong when updating the user");
                }
            }
        }, 100);
    };

    const deleteCompanyUser = (userUID) => {
        if (userUID === user.uid) {
            alert("Cannot delete yourself");
            return;
        }
        pendingDeleteUID.value = userUID;
        deleteError.value = '';
        showDeleteModal.value = true;
    };

    const confirmDeleteUser = async () => {
        deleteLoading.value = true;
        deleteError.value = '';
        const body = {
            uid: user.uid,
            userUID: [pendingDeleteUID.value]
        };
        let responseData;
        try {
            responseData = await Users.delete('', body);
        } catch (error) {
            console.error('Error deleting company user:', error);
            deleteError.value = 'Something went wrong when deleting the user';
            deleteLoading.value = false;
            return;
        }
        deleteLoading.value = false;
        if (responseData.status === 'success') {
            companyUsers.value = companyUsers.value.filter(u => u.uid !== pendingDeleteUID.value);
            showDeleteModal.value = false;
            pendingDeleteUID.value = null;
        } else {
            deleteError.value = responseData.message || 'Something went wrong when deleting the user';
        }
    };

    const cancelDeleteUser = () => {
        showDeleteModal.value = false;
        pendingDeleteUID.value = null;
        deleteError.value = '';
    };

    const handleTableAction = ({ action, row }) => {
        const companyUser = companyUsers.value.find(u => u.uid === row.id);

        if (action === 'Edit' && canEdit.value) {
            selectedUser.value = { ...companyUser };
            editUserModal.value = true;
        } else if (action === 'Delete' && canDelete.value) {
            deleteCompanyUser(row.id);
        }
    };
</script>

<template>
    <div v-if="!hasAccess" class="flex flex-col items-center justify-center h-full">
        <h1 class="text-3xl md:text-5xl my-4 md:my-6 font-bold text-mc-red">Access Denied</h1>
        <p class="text-xl text-ink-muted dark:text-on-dark-muted">You do not have permission to access this page.</p>
        <p class="text-lg text-ink-muted dark:text-on-dark-muted mt-2">Required permissions:
            <span class="font-mono bg-mc-steel/30 dark:bg-white/10 px-2 py-1 rounded-mc-sm text-mc-ink dark:text-on-dark">addCompanyUser</span>,
            <span class="font-mono bg-mc-steel/30 dark:bg-white/10 px-2 py-1 rounded-mc-sm text-mc-ink dark:text-on-dark">editCompanyUser</span>, or
            <span class="font-mono bg-mc-steel/30 dark:bg-white/10 px-2 py-1 rounded-mc-sm text-mc-ink dark:text-on-dark">deleteCompanyUser</span>
        </p>
    </div>
    <div v-else>
        <h1 class="text-2xl font-bold text-mc-ink dark:text-on-dark mb-4 md:mb-6">My Company</h1>
        <div class="flex-grow overflow-y-auto px-2 md:px-0">
        <!-- Company Info Section -->
        <div class="space-y-4 mb-6 text-mc-ink dark:text-on-dark">
            <div class="flex flex-col md:flex-row md:items-center">
                <h3 class="font-bold w-20">Name:</h3>
                <p class="md:ml-6">{{ company.name }}</p>
            </div>
            <div class="flex flex-col md:flex-row md:items-center">
                <h3 class="font-bold w-20">Status:</h3>
                <p class="md:ml-5">{{ company.status }}</p>
            </div>
            <div class="space-y-2">
                <h3 class="font-bold">Logo:</h3>
                <img :src=company.logo alt="logo" class="w-36 md:w-52 mb-4 md:mb-8">
            </div>
            <div class="space-y-2">
                <h3 class="font-bold">Icon:</h3>
                <img :src=company.icon alt="logo" class="w-8 mb-4 md:mb-8">
            </div>
        </div>

        <!-- Users Section -->
        <div class="mb-8 md:mb-12 md:pr-12">
            <div class="flex items-center justify-between mb-4">
                <h3 class="font-bold text-xl text-mc-ink dark:text-on-dark">Users</h3>
                <div v-if="canAdd">
                    <button @click="createUserModal = true" class="default-button">+ add user</button>
                </div>
            </div>

            <GenericTable2
                :data="tableData"
                :actions="tableActions"
                :actionException="actionException"
                :loading="loading"
                :showId="false"
                :responsive="true"
                @ActionSelected="handleTableAction"
            />
            </div>
        </div>

        <CreateUser v-if="createUserModal" @close="createUserModal = false" @submitForm="addCompanyUser"></CreateUser>
        
        <CreateUser
            v-if="editUserModal"
            :userDetails="selectedUser"
            @close="editUserModal = false; selectedUser = null"
            @submitForm="editCompanyUser"
        ></CreateUser>

        <ConfirmModal
            v-if="showDeleteModal"
            title="Delete User"
            :message="deleteModalMessage"
            confirm-label="Delete"
            variant="danger"
            :loading="deleteLoading"
            :error="deleteError"
            @confirm="confirmDeleteUser"
            @cancel="cancelDeleteUser"
        />
    </div>
</template>
